import {ApolloLink, HttpLink, Observable} from '@apollo/client';
import {createOperation} from '@apollo/client/link/utils';
import {GraphQlError} from '@sail/data';

import type {ObservableSubscription} from '@apollo/client';
import type {Fetch, GraphQlDocument, InternalOperation} from '@sail/data';
import type {DefinitionNode, ExecutionResult, SelectionNode} from 'graphql';
import wrapFetchWithErrors from '../errors/wrapFetchWithErrors';

import routeTo from '../../../../schema/routeTo.json';

type ExecutionResultWithResponse = ExecutionResult & {
  response: Response;
};

const defaultSubscriptionError = new GraphQlError(
  'Subscription completed with no value',
);

export const routeToNorthwest = new Set();

export function computeRouteForOperation(
  operationName: string,
  operationType: 'query' | 'mutation' | 'subscription',
): string {
  if (routeToNorthwest.has(operationName)) {
    return '';
  }

  const fields = routeTo[operationType];
  if (fields.hasOwnProperty(operationName)) {
    return fields[operationName as keyof typeof fields];
  }

  return '';
}

export function createHttpLink(endpoint: string, fetch: Fetch, url: string) {
  return new HttpLink({
    fetch,

    uri: (operation: InternalOperation) => {
      // If the endpoint looks like an absolute URL it is very likely we're
      // going to end up performing CORS requests, so to prevent pre-flight
      // requests over and over, we need to keep the URL stable.
      if (/^(?:https?:)?\/\//.test(endpoint)) {
        return `${endpoint}${url}`;
      }

      return `${endpoint}${url}?name=${operation.operationName}`;
    },

    includeUnusedVariables: true,
  });
}

type CreateHttpLinkWrapperFn = (
  endpoint: string,
  fetch: Fetch,
  url: string,
) => ApolloLink;

export default function createTerminatingLink(
  // A way to inject different logic for old and revamped APIs.
  createHttpLinkWrapper: CreateHttpLinkWrapperFn,
  endpoint: string,
) {
  const httpLinksByFetch: WeakMap<
    Fetch,
    Record<string, ApolloLink>
  > = new WeakMap();

  return new ApolloLink(function terminatingLink(operation: InternalOperation) {
    return new Observable(function terminatingLinkSubscriptionFn(observer) {
      const perRoute: Record<string, SelectionNode[]> = {};
      const query: GraphQlDocument<unknown, unknown, unknown> = operation.query;
      const defs = query.definitions;

      const operationDefinition = defs.find((d) => {
        return d.kind === 'OperationDefinition';
      });

      const nonOperationDefinitions: DefinitionNode[] = defs.filter((d) => {
        return d.kind !== 'OperationDefinition';
      });

      if (!operationDefinition) {
        throw new Error(
          'Could not detect any GraphQL operation (query or mutation)',
        );
      }

      const subscriptions: ObservableSubscription[] = [];

      operationDefinition.selectionSet.selections.forEach((s) => {
        const name = s.kind === 'Field' ? s.name.value : '';

        const route = computeRouteForOperation(
          name,
          operationDefinition.operation,
        );
        if (perRoute.hasOwnProperty(route)) {
          perRoute[route].push(s);
        } else {
          perRoute[route] = [s];
        }
      });

      const promises = Object.keys(perRoute).map(
        function createRouteRequestPromise(route) {
          const newOperation = createOperation(operation.getContext(), {
            ...operation,

            query: {
              ...query,

              definitions: nonOperationDefinitions.concat({
                ...operationDefinition,

                selectionSet: {
                  ...operationDefinition.selectionSet,
                  selections: perRoute[route],
                },
              }),
            },
          });

          return new Promise<ExecutionResultWithResponse>(
            function routeRequestExecution(resolve, reject) {
              const context = operation.getContext();
              const customFetch = context.fetch || fetch;
              const httpLinks = httpLinksByFetch.get(customFetch) || {};

              httpLinksByFetch.set(customFetch, httpLinks);

              let partialResult: ExecutionResult = {
                data: null,
                errors: [defaultSubscriptionError],
              };

              if (!httpLinks.hasOwnProperty(route)) {
                httpLinks[route] = createHttpLinkWrapper(
                  endpoint,
                  wrapFetchWithErrors(customFetch),
                  route,
                );
              }

              const observable = httpLinks[route].request(newOperation);

              if (!observable) {
                throw new Error('HttpLink did not return an observable');
              }

              subscriptions.push(
                observable.subscribe({
                  next: function routeRequestNext(value) {
                    partialResult = value;
                  },

                  error: function routeRequestError(error) {
                    reject(error);
                  },

                  complete: function routeRequestComplete() {
                    resolve({
                      data: partialResult.data,
                      errors: partialResult.errors,
                      response: newOperation.getContext().response,
                    });
                  },
                }),
              );
            },
          );
        },
      );

      const promise = Promise.all(promises).then((values) => {
        let resultData: ExecutionResult['data'] = {};
        let resultErrors: NonNullable<ExecutionResult['errors']> = [];
        const responses: Response[] = [];

        values.forEach(({data, errors, response}) => {
          if (data) {
            resultData = {...resultData, ...data};
          }

          if (errors) {
            resultErrors = [...resultErrors, ...errors];
          }

          responses.push(response);
        });

        const result: ExecutionResult = resultErrors.length
          ? {data: resultData, errors: resultErrors}
          : {data: resultData};

        operation.setContext({responses});

        observer.next(result);
        observer.complete();
      });

      promise.catch((error) => {
        observer.error(error);
      });

      return () => {
        subscriptions.forEach((subscription) => {
          subscription.unsubscribe();
        });
      };
    });
  });
}
